const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, '');

const getConfiguredApiBaseUrl = (): string => {
  const sharedBaseUrl = process.env.REACT_APP_API_BASE_URL;

  if (process.env.NODE_ENV === 'development') {
    return trimTrailingSlash(
      process.env.REACT_APP_API_BASE_URL_DEV ||
      sharedBaseUrl ||
      'http://localhost:8082'
    );
  }

  const configuredProductionBaseUrl =
    process.env.REACT_APP_API_BASE_URL_PROD || sharedBaseUrl;

  if (configuredProductionBaseUrl) {
    return trimTrailingSlash(configuredProductionBaseUrl);
  }

  const safeFallback =
    typeof window !== 'undefined' && window.location?.origin
      ? trimTrailingSlash(window.location.origin)
      : '';

  console.error(
    '[apiConfig] Missing production API base URL. Set REACT_APP_API_BASE_URL_PROD explicitly.'
  );

  return safeFallback;
};

const API_BASE_URL = getConfiguredApiBaseUrl();

export const ENDPOINTS = {
    LOGIN: `${API_BASE_URL}/login`, // Your login endpoint
    LOGOUT: `${API_BASE_URL}/logout`, // Your logout endpoint
    REGISTER: `${API_BASE_URL}/register`, // Registration endpoint
    RESEND_REGISTRATION_VERIFICATION: `${API_BASE_URL}/register/resend-verification`,
    VERIFY_REGISTRATION: (token: string) => `${API_BASE_URL}/verify-registration/${encodeURIComponent(token)}`,
    REQUEST_COMPANY_TOKEN: `${API_BASE_URL}/request-company-token`, // Company token request endpoint
    AUTO_COMPANY_TOKEN: `${API_BASE_URL}/company-token/auto`, // Auto-create company token for authenticated users
    CREATE_COMPANY: `${API_BASE_URL}/create-company`, // Company creation endpoint
    CREATE_OWN_COMPANY: `${API_BASE_URL}/companies/self`,
    ACTIVE_SUBSCRIPTION_PLANS: `${API_BASE_URL}/subscription-plans/active`,
    UPGRADE_OWN_COMPANY_PLAN: (companyId: number | string) => `${API_BASE_URL}/subscriptions/self/companies/${companyId}/upgrade`,
    SEND_COMPANY_TOKEN: `${API_BASE_URL}/send-company-token`, // Sales rep endpoint to send tokens
    PENDING_COMPANY_REQUESTS: `${API_BASE_URL}/pending-company-requests`, // Get pending requests for sales reps
    COMPANY_TOKEN_DETAILS: (token: string) => `${API_BASE_URL}/company-token-details/${token}`, // Get token details for pre-filling form
    UPLOAD_COMPANY_LOGO: `${API_BASE_URL}/upload-company-logo`, // Upload company logo
    UPLOAD_USER_AVATAR: `${API_BASE_URL}/users/me/avatar`, // Upload profile avatar
    FORGOT_PASSWORD: `${API_BASE_URL}/resetpassword`,
    RESET_PASSWORD: `${API_BASE_URL}/resetpassword`,
  
      GET_ALL_JOBS: `${API_BASE_URL}/jobs`,
  
      // Access Token
      GET_ACCESS_TOKEN: `${API_BASE_URL}/access-tokens`,
  
  
      // AREA Endpoints
      CHECK_AREA: `${API_BASE_URL}/area/check-inside-area`,
      CREATE_AREA: `${API_BASE_URL}/area/create-area`,
      CREATE_AREA_GPS: `${API_BASE_URL}/area/create-area-gps`,
      GET_AREAS: `${API_BASE_URL}/areas`,
      GET_AREA_TYPES: `${API_BASE_URL}/area/get-types`,
      GET_SHARES: `${API_BASE_URL}/area/get-shares`, // Requires: areaId
      SHARE_AREA: `${API_BASE_URL}/area/share`, // Requires: areaId, userIds[]
      DELETE_AREA: `${API_BASE_URL}/area/delete-area`, // Requires: areaID
  
      // Company Endpoints
      GET_COMPANIES: `${API_BASE_URL}/companies`,
      GET_MEMBERSHIPS: `${API_BASE_URL}/memberships/user`,
      SWITCH_COMPANY: `${API_BASE_URL}/memberships/switch-company`,
  
      // Devices Endpoints
      GET_DEVICES: `${API_BASE_URL}/devices`,
      GET_DEVICES_BY_USER: `${API_BASE_URL}/devices`, // Requires '/##' as User Id
      GET_ROBOT_DETAILS: `${API_BASE_URL}/device`, // Requires '/##' Number of the Robot
      CREATE_DEVICE: `${API_BASE_URL}/device`, // Requires: Device except "id"
      UPDATE_DEVICE: `${API_BASE_URL}/device/update`, // Requires: Device
      DELETE_DEVICE: `${API_BASE_URL}/device/delete`, // Requires: Device.id???
      DEVICE_USAGE: (id: number | string) => `${API_BASE_URL}/devices/${id}/usage`,
      DEVICE_TELEMETRY_HISTORY: (id: number | string) => `${API_BASE_URL}/devices/${id}/telemetry-history`,
      DEVICE_AUTH_TOKENS: (id: number | string) => `${API_BASE_URL}/devices/${id}/auth-tokens`,
      DEVICE_AUTH_TOKEN_REVOKE: (deviceId: number | string, tokenId: number | string) =>
          `${API_BASE_URL}/devices/${deviceId}/auth-tokens/${tokenId}/revoke`,
      DEVICE_AUTH_TOKEN_ROTATE: (deviceId: number | string, tokenId: number | string) =>
          `${API_BASE_URL}/devices/${deviceId}/auth-tokens/${tokenId}/rotate`,

      // User API tokens (Configuration → API Tokens; used for ethernet/remote device control)
      EXTERNAL_AREAS: `${API_BASE_URL}/external/v1/areas`,
      EXTERNAL_PATTERNS: `${API_BASE_URL}/external/v1/patterns`,
      USER_API_TOKENS: `${API_BASE_URL}/user-api-tokens`,
      USER_API_TOKEN_REVOKE: (tokenId: number | string) =>
          `${API_BASE_URL}/user-api-tokens/${tokenId}/revoke`,
      USER_API_TOKEN_ROTATE: (tokenId: number | string) =>
          `${API_BASE_URL}/user-api-tokens/${tokenId}/rotate`,
      EXTERNAL_DEVICES: `${API_BASE_URL}/external/v1/devices`,
      EXTERNAL_MISSION_DRAFTS: `${API_BASE_URL}/external/v1/mission-drafts`,
      EXTERNAL_AREA_MEASUREMENTS: (areaId: number | string) =>
          `${API_BASE_URL}/external/v1/areas/${areaId}/measurements`,
      EXTERNAL_PATTERN_POINT_MEASUREMENTS: (pointId: number | string) =>
          `${API_BASE_URL}/external/v1/pattern-points/${pointId}/measurements`,
      EXTERNAL_DEVICE_MEASUREMENTS: (deviceId: number | string) =>
          `${API_BASE_URL}/external/v1/devices/${deviceId}/measurements`,
      DEVICE_VIDEO_STREAMS: (id: number | string) => `${API_BASE_URL}/devices/${id}/video-streams`,
      DEVICE_VIDEO_STREAM: (deviceId: number | string, cameraKey: string) =>
          `${API_BASE_URL}/devices/${deviceId}/video-streams/${encodeURIComponent(cameraKey)}`,
  
      // Jobs Endpoints
      GET_JOBS_BY_USERID_BY_DATE: `${API_BASE_URL}/jobs/by-userId-by-date`,
  
      // Missions
      ADD_MISSION: `${API_BASE_URL}/mission/add-mission`,
      GET_MISSIONS_BY_USERID_BY_DATE: `${API_BASE_URL}/mission/get-missions`,
      MISSIONS: `${API_BASE_URL}/missions`,
      MODULE_DEFINITIONS: `${API_BASE_URL}/module-definitions`,
      DEVICE_CAPABILITY_DEFINITIONS: `${API_BASE_URL}/device-capability-definitions`,
      VEHICLE_PROFILES: `${API_BASE_URL}/vehicle-profiles`,
      MODULE_DEFINITION_BY_ID: (id: number | string) => `${API_BASE_URL}/module-definitions/${id}`,
      MISSION_IMPORT_ARDUPILOT_PREVIEW: `${API_BASE_URL}/missions/import-ardupilot/preview`,
      MISSION_EXPORT_ARDUPILOT: (id: number | string, format: 'qgc_plan' | 'qgc_wpl' = 'qgc_plan') =>
          `${API_BASE_URL}/missions/${id}/export-ardupilot?format=${format}`,
      MISSION_DETAIL: (id: number | string) => `${API_BASE_URL}/missions/${id}`,
      MISSION_POINTS: (id: number | string) => `${API_BASE_URL}/missions/${id}/points`,
      MISSION_IMPORT_PATTERN_POINTS: (id: number | string) => `${API_BASE_URL}/missions/${id}/points/import-pattern`,
      MISSION_RECALCULATE_PATH: (id: number | string) => `${API_BASE_URL}/missions/${id}/recalculate-path`,
      MISSION_START: (id: number | string) => `${API_BASE_URL}/missions/${id}/start`,
      MISSION_CANCEL: (id: number | string) => `${API_BASE_URL}/missions/${id}/cancel`,
      MISSION_COMPLETE: (id: number | string) => `${API_BASE_URL}/missions/${id}/complete`,
      MISSION_ARCHIVE: (id: number | string) => `${API_BASE_URL}/missions/${id}/archive`,
      MISSION_RESTORE: (id: number | string) => `${API_BASE_URL}/missions/${id}/restore`,
  
      // Sensor Data
      GET_DATA: `${API_BASE_URL}/sensordata`,
      GET_DATA_BY_AREA_ID_BY_DATE: `${API_BASE_URL}/sensordata/data-by-area-id-by-date`,
      GET_DATA_BY_DEVICE_BY_DATE: `${API_BASE_URL}/sensordata/by-device-by-date`,
      GET_DATA_BY_DEVICE_ID: `${API_BASE_URL}/sensordata/by-device-id`,
      GET_DATA_BY_TOOL_ID_BY_DATE: `${API_BASE_URL}/sensordata/by-tool-id`,
      GET_DATA_BY_TYPE_BY_POLYGON_BY_DATE: `${API_BASE_URL}/sensordata/data-by-type-by-polygon-by-date`,
      GET_LAST_DEPLOYMENT: `${API_BASE_URL}/sensordata/get-last-deployment`,
      GET_LAST_DEPLOYMENT_DATA: `${API_BASE_URL}/sensordata/get-last-deployment-data`,
      GET_LAST_DATA_BY_AREA: `${API_BASE_URL}/sensordata/get-last-data-by-area`,
      GET_DATES_BY_AREA_ID: `${API_BASE_URL}/sensordata/dates-by-area-id`,
      GET_AVAILABLE_DATES_BY_AREA_ID: `${API_BASE_URL}/sensordata/available-dates-by-area-id`,
      GET_SCORE: `${API_BASE_URL}/calculate-score`,
      CREATE_ANALYSIS: `${API_BASE_URL}/analysis/create`,
      GET_ANALYSIS_BY_SENSOR: `${API_BASE_URL}/analysis/get-by-sensor`,
      CREATE_RASTER_MAP: `${API_BASE_URL}/sensordata/create-raster-map`,
      UPDATE_LOCATION: `${API_BASE_URL}/sensordata/update-position`,
  
      // Template Endpoints
      GET_TEMPLATES_BY_AREA_ID_SURFACE_ID: `${API_BASE_URL}/template/getByAreaIdSurfaceId`,
      CREATE_TEMPLATE: `${API_BASE_URL}/template/create`,
      DELETE_TEMPLATE: `${API_BASE_URL}/template/delete`, // Requires: templateID
  
      // Tool Endpoints
      GET_TOOLS_BY_DEVICE_ID: `${API_BASE_URL}/tools/getByDevice`,
      CREATE_TOOL: `${API_BASE_URL}/tools/create`, // Requires: name, hardwareId, status, cycles, toolTypeId
      UPDATE_TOOL: `${API_BASE_URL}/tools/update`, // Requires: deviceId, name, hardwareId, status, cycles, toolTypeId
      DELETE_TOOL: `${API_BASE_URL}/tools/delete`, // Requires: deviceId
  
      // Tool Type Endpoints
      GET_TOOL_TYPES: `${API_BASE_URL}/tool-types`,
  
      // User Endpoints
      GET_USER: `${API_BASE_URL}/getuser`, // Add the GetUser endpoint
      GET_COMPANY_USERS: `${API_BASE_URL}/users`, // Add All Users that share company with this user
      DELETE_SELF: `${API_BASE_URL}/users/me`, // Delete current user account
      UPDATE_DASHBOARD_SETTINGS: `${API_BASE_URL}/users/me/dashboard-settings`,
      WHATSAPP_QR: `${API_BASE_URL}/whatsapp/qr`,
      WHATSAPP_QR_CONTEXT: `${API_BASE_URL}/whatsapp/qr-context`,
      WHATSAPP_NUMBERS: `${API_BASE_URL}/whatsapp/numbers`,
      WHATSAPP_VERIFY: (token: string) => `${API_BASE_URL}/whatsapp/verify-number?token=${encodeURIComponent(token)}`,
  
      // Conversation Endpoints
      START_CONVERSATION: `${API_BASE_URL}/conversation/startConversation`,
      CONVERSATIONS: `${API_BASE_URL}/conversations`,
      CONVERSATION_MESSAGES: (conversationId: number | string) => `${API_BASE_URL}/conversations/${conversationId}/messages`,
      CONVERSATION_DETAIL: (conversationId: number | string) => `${API_BASE_URL}/conversations/${conversationId}`,

      // Context discovery endpoints
      AREA_TYPES_V2: `${API_BASE_URL}/area-types`,
      AREAS_V2: `${API_BASE_URL}/areas`,
      AREA_DETAIL: (areaId: number | string) => `${API_BASE_URL}/areas/${areaId}`,
      AREA_ANALYSIS: (areaId: number | string) => `${API_BASE_URL}/areas/${areaId}/analysis`,
      AREA_ANALYSES: (areaId: number | string) => `${API_BASE_URL}/areas/${areaId}/analyses`,
      CREATE_AREA_V2: `${API_BASE_URL}/areas`,
      UPDATE_AREA_V2: (areaId: number | string) => `${API_BASE_URL}/areas/${areaId}`,
      DELETE_AREA_V2: (areaId: number | string) => `${API_BASE_URL}/areas/${areaId}`,
      AREA_PATTERNS: (areaId: number | string) => `${API_BASE_URL}/areas/${areaId}/patterns`,
      AREA_HEALTH_STATUS: `${API_BASE_URL}/areas/health-status`,
      PATTERN_POINTS: (patternId: number | string) => `${API_BASE_URL}/patterns/${patternId}/points`,
      PATTERNS: `${API_BASE_URL}/patterns`,
      MEASUREMENTS_V2: `${API_BASE_URL}/measurements`,
      MEASUREMENTS_AREA_COUNTS: `${API_BASE_URL}/measurements/area-counts`,
      MEASUREMENT_DETAIL: (id: number | string) => `${API_BASE_URL}/measurements/${id}`,
      MEASUREMENT_TEXTS: (id: number | string) => `${API_BASE_URL}/measurements/${id}/texts`,
      MEASUREMENT_TEXT: (id: number | string, textId: number | string) => `${API_BASE_URL}/measurements/${id}/texts/${textId}`,
      MEASUREMENT_FILES_BATCH_URLS: (id: number | string) => `${API_BASE_URL}/measurements/${id}/files/batch-urls`,
      MEASUREMENT_WEATHER: (id: number | string) => `${API_BASE_URL}/measurements/${id}/weather`,
      MEASUREMENT_WEATHER_REFRESH: (id: number | string) => `${API_BASE_URL}/measurements/${id}/weather/refresh`,
      MEASUREMENT_ANALYSIS: (id: number | string) => `${API_BASE_URL}/measurements/${id}/analysis`,
      MEASUREMENT_ANALYSES: (id: number | string) => `${API_BASE_URL}/measurements/${id}/analyses`,
      MEASUREMENT_LEARNING: (id: number | string) => `${API_BASE_URL}/measurements/${id}/learning`,
      AREA_LEARNING: (id: number | string) => `${API_BASE_URL}/areas/${id}/learning`,
      POINT_LEARNING: (id: number | string) => `${API_BASE_URL}/pattern-points/${id}/learning`,
      MEASUREMENTS_BULK_UPDATE_AREA: `${API_BASE_URL}/measurements/bulk-update-area`,
      MEASUREMENTS_BULK_UPDATE_PATTERN: `${API_BASE_URL}/measurements/bulk-update-pattern`,
      MEASUREMENTS_BATCH_FILES_ZIP: `${API_BASE_URL}/measurements/batch-files/zip`,
      MEASUREMENT_UPDATE_LOCATION: (id: number | string) => `${API_BASE_URL}/measurements/${id}/location`,
      MEASUREMENTS_IMPORT: `${API_BASE_URL}/measurements/import`,
      MEASUREMENTS_AI_PARSE: `${API_BASE_URL}/measurements/ai-parse`,
      MEASUREMENTS_AI_REFINE: `${API_BASE_URL}/measurements/ai-refine`,

      // Dashboard
      DASHBOARD_OVERVIEW: (companyId: number | string) => `${API_BASE_URL}/dashboard/overview?companyId=${companyId}`,
      DASHBOARD_INSIGHTS: (companyId: number | string) => `${API_BASE_URL}/dashboard/insights?companyId=${companyId}`,
      DASHBOARD_FAVORITE_AREA: (companyId: number | string) => `${API_BASE_URL}/dashboard/favorite-area?companyId=${companyId}`,
      NOTIFICATIONS: (companyId: number | string, status: string = 'active') =>
        `${API_BASE_URL}/notifications?companyId=${companyId}&status=${status}`,
      NOTIFICATION_HIDE: (id: number | string) => `${API_BASE_URL}/notifications/${id}/hide`,
      NOTIFICATION_ARCHIVE: (id: number | string) => `${API_BASE_URL}/notifications/${id}/archive`,
      NOTIFICATION_DELETE: (id: number | string) => `${API_BASE_URL}/notifications/${id}`,

      // Metrics & report template wizard
      METRICS: `${API_BASE_URL}/metrics`,
      METRIC_DETAIL: (id: number | string) => `${API_BASE_URL}/metrics/${id}`,
      METRICS_DISCOVER: `${API_BASE_URL}/metrics/discover`,
      METRICS_BULK: `${API_BASE_URL}/metrics/bulk`,
      AREA_TYPE_METRICS: `${API_BASE_URL}/area-type-metrics`,
      AREA_TYPE_METRIC_DETAIL: (id: number | string) => `${API_BASE_URL}/area-type-metrics/${id}`,
      REPORT_TEMPLATE_WIZARD_META: `${API_BASE_URL}/report-templates/wizard/meta`,
      REPORT_TEMPLATE_WIZARD_GENERATE: `${API_BASE_URL}/report-templates/wizard/generate`,
      REPORT_TEMPLATES: `${API_BASE_URL}/report-templates`,
      REPORT_TEMPLATE_DETAIL: (id: number | string) => `${API_BASE_URL}/report-templates/${id}`,
      REPORTS: `${API_BASE_URL}/reports`,
      REPORT_DETAIL: (id: number | string) => `${API_BASE_URL}/reports/${id}`,
      REPORT_SHARES: (id: number | string) => `${API_BASE_URL}/reports/${id}/shares`,
      REPORT_SECTION_KPI: `${API_BASE_URL}/reports/sections/kpi`,
      REPORT_SECTION_TABLE: `${API_BASE_URL}/reports/sections/table`,
      REPORT_SECTION_LINE: `${API_BASE_URL}/reports/sections/line`,
      REPORT_SECTION_PATTERN_POINT_LINE: `${API_BASE_URL}/reports/sections/pattern-point-line`,
      REPORT_SECTION_AREA_HIERARCHY: `${API_BASE_URL}/reports/sections/area-hierarchy`,
      REPORT_SECTION_HEATMAP: `${API_BASE_URL}/reports/sections/heatmap`,
      REPORT_SECTION_ANIMATED_HEATMAP: `${API_BASE_URL}/reports/sections/animated-heatmap`,
      REPORT_SECTION_ANIMATED_PATTERN_POINTS: `${API_BASE_URL}/reports/sections/animated-pattern-points`,
      REPORT_SECTION_PATTERN_LIST: `${API_BASE_URL}/reports/sections/pattern-list`,
      REPORT_SECTION_CALENDAR_AVAILABILITY: `${API_BASE_URL}/reports/sections/calendar-availability`,
      REPORT_SECTION_MEASUREMENT_LIST: `${API_BASE_URL}/reports/sections/measurement-list`,

      // Operations & Orchestration
      OPERATIONS: `${API_BASE_URL}/operations`,
      OPERATION_DETAIL: (id: number | string) => `${API_BASE_URL}/operations/${id}`,
      OPERATION_MISSIONS: (id: number | string) => `${API_BASE_URL}/operations/${id}/missions`,
      OPERATION_MISSION_REMOVE: (id: number | string, omId: number | string) => `${API_BASE_URL}/operations/${id}/missions/${omId}`,
      OPERATION_START: (id: number | string) => `${API_BASE_URL}/operations/${id}/start`,
      OPERATION_CANCEL: (id: number | string) => `${API_BASE_URL}/operations/${id}/cancel`,

      // Device Commands & Telemetry
      DEVICE_COMMANDS: `${API_BASE_URL}/device-commands`,
      DEVICE_COMMAND_DETAIL: (id: number | string) => `${API_BASE_URL}/device-commands/${id}`,
      DEVICE_TELEMETRY: `${API_BASE_URL}/device-telemetry`,
      DEVICE_TELEMETRY_STREAM: `${API_BASE_URL}/device-telemetry/stream`,

      // Activity tracking
      ACTIVITY_TRACK: `${API_BASE_URL}/activity`,

      // SLAM Maps
      SLAM_MAPS: `${API_BASE_URL}/slam-maps`,
      SLAM_MAP_DETAIL: (id: number | string) => `${API_BASE_URL}/slam-maps/${id}`,
      SLAM_MAP_IMAGE_UPLOAD: (id: number | string) => `${API_BASE_URL}/slam-maps/${id}/image`,
      SLAM_MAP_IMAGE_URL: (id: number | string) => `${API_BASE_URL}/slam-maps/${id}/image-url`,
      SLAM_MAP_IMAGE_STREAM: (id: number | string) => `${API_BASE_URL}/slam-maps/${id}/image-stream`,
      MISSION_SLAM_MAPS: (missionId: number | string) => `${API_BASE_URL}/missions/${missionId}/slam-maps`,
      MISSION_SLAM_MAP_DETACH: (missionId: number | string, slamMapId: number | string) =>
          `${API_BASE_URL}/missions/${missionId}/slam-maps/${slamMapId}`,

      // Indoor Marker (AprilTag anchor)
      MISSION_INDOOR_MARKERS_GENERATE: (missionId: number | string) =>
          `${API_BASE_URL}/missions/${missionId}/indoor-markers/generate`,
      MISSION_INDOOR_MARKERS_SHEET: (missionId: number | string) =>
          `${API_BASE_URL}/missions/${missionId}/indoor-markers/sheet`,
      AREA_INDOOR_MARKERS: (areaId: number | string) =>
          `${API_BASE_URL}/areas/${areaId}/indoor-markers`,

      // General Markers
      MARKERS: `${API_BASE_URL}/markers`,
      MARKER_DETAIL: (id: number | string) => `${API_BASE_URL}/markers/${id}`,
      DEVICE_MARKER_CAPTURE: (deviceId: number | string) =>
          `${API_BASE_URL}/devices/${deviceId}/markers/capture`,

      // SLAM maps available for selection for an area (assigned + orphaned company-wide)
      AREA_SLAM_MAPS: (areaId: number | string) =>
          `${API_BASE_URL}/areas/${areaId}/slam-maps`,

      // Latest SLAM map for a (mixed) area
      AREA_SLAM_MAP_LATEST: (areaId: number | string) =>
          `${API_BASE_URL}/areas/${areaId}/slam-maps/latest`,

      // Company root area configuration
      COMPANY_ROOT_AREA: (companyId: number | string) =>
          `${API_BASE_URL}/companies/${companyId}/root-area`,
  };
  
export function buildFileUrl (filename: string): string {
    return API_BASE_URL+'/getfileurl/'+filename;
}
  
export default API_BASE_URL;
